import { createModel } from '@rematch/core';
import { selectors } from './selector';
import { RootState } from '../../store';
import { CompositeDrug } from '../../models/composite-drug';
import {
    getCompositeDrugs,
    searchCompositeDrugs
} from '../../apis/emr-data';

interface ProcessedData {
    byCode: Dictionary<number>;
    byMedId: Dictionary<CompositeDrug>;
    byMedNameId: Dictionary<number[]>;
}

export interface State {
    byCode: Dictionary<number>; // code: medNameId + medDoseForm + medStrength + medRoute (if medStrength empty => medical-supply + medNameId)
    byMedId: Dictionary<CompositeDrug>;
    byMedNameId: Dictionary<number[]>;
    loading: Dictionary<boolean>;
    searchedTextHash: Dictionary<boolean>;
}

export const compositeDrugs = createModel<State>({
    state: {
        byCode: {},
        byMedId: {},
        byMedNameId: {},
        loading: {},
        searchedTextHash: {}
    },
    reducers: {
        update: (
            state: State,
            data: Undefinable<MixType<CompositeDrug>>,
            loadingKey?: string
        ): State => {
            const processedData = processData(data, state.byMedNameId);
            return {
                ...state,
                byCode: {
                    ...state.byCode,
                    ...processedData.byCode
                },
                byMedId: {
                    ...state.byMedId,
                    ...processedData.byMedId
                },
                byMedNameId: {
                    ...state.byMedNameId,
                    ...processedData.byMedNameId
                },
                ...loadingKey && {
                    loading: {
                        ...state.loading,
                        [loadingKey]: false
                    }
                }
            };
        },
        loading: (state: State, loadingKey: string, loading: boolean): State => ({
            ...state,
            loading: {
                ...state.loading,
                [loadingKey]: loading
            }
        }),
        setSearched: (state: State, text: string): State => ({
            ...state,
            searchedTextHash: {
                ...state.searchedTextHash,
                [text]: true
            }
        })
    },
    effects: () => ({
        async getCompositeDrugsAsync(ids: Undefinable<number>[], rootState: RootState) {
            const notExistedIds = selectors.getOtherCompositeDrugIds(rootState.compositeDrugs, ids);
            if (notExistedIds.length > 0) {
                try {
                    const data = await getCompositeDrugs(notExistedIds);
                    this.update(data);
                } catch (e) {
                    window.console.log(e);
                }
            }
        },
        async searchCompositeDrugsAsync(text: string, rootState: RootState) {
            const loading = selectors.getLoading(rootState.compositeDrugs, `searchCompositeDrugsAsync---${text}`);
            if (!loading && !selectors.checkSearched(rootState.compositeDrugs, text)) {
                this.loading(`searchCompositeDrugsAsync---${text}`, true);
                const data = await searchCompositeDrugs(text);
                this.update(data, `searchCompositeDrugsAsync---${text}`);
                this.loading(`searchCompositeDrugsAsync---${text}`, false);
                this.setSearched(text);
            }
        }
    })
});

export const processData = (
    data: Undefinable<MixType<CompositeDrug>>,
    initalByMedNameId: Dictionary<number[]>
): ProcessedData => {
    const byCode = {} as Dictionary<number>;
    const byMedId = {} as Dictionary<CompositeDrug>;
    const byMedNameId = {} as Dictionary<number[]>;

    if (data) {
        if (Array.isArray(data)) {
            for (let i = 0; i < data.length; i++) {
                if (data[i].medStrength) {
                    byCode[[data[i].medNameId, data[i].medDoseForm, data[i].medStrength, data[i].medRoute].join('---')] = data[i].medId;
                } else {
                    byCode[`medical-supply---${data[i].medNameId}`] = data[i].medId;
                }
                byMedId[data[i].medId] = data[i];
                if (!byMedNameId[data[i].medNameId]) {
                    byMedNameId[data[i].medNameId] = [...initalByMedNameId[data[i].medId] || []];
                }
                byMedNameId[data[i].medNameId]!.push(data[i].medId);
            }
        } else {
            if (data.medStrength) {
                byCode[[data.medNameId, data.medDoseForm, data.medStrength, data.medRoute].join('---')] = data.medId;
            } else {
                byCode[`medical-supply---${data.medNameId}`] = data.medId;
            }
            byMedId[data.medId] = data;
            byMedNameId[data.medNameId] = [data.medNameId];
        }
    }

    return {
        byCode,
        byMedId,
        byMedNameId
    };
};
